import { useTranslation } from "next-i18next";
import Image from "next/image";
import clsx from "clsx";

import { GetLeaderboardRankingsQuery } from "@/graphql/types";
import { NO_IMAGE_URL } from "@/utils/constants";
import { getUserUrl } from "@/utils/getUserInfo";
import { getAuthUser } from "@/utils/oAuth/storage";
import ConditionalLink from "@/components/hoc/ConditionalLink";
import { displayEntryScore } from "@/components/LeaderboardCard/helpers";

interface LeaderboardEntryProps {
  fallbackAvatarUrl?: string;
  forceMine?: boolean;
  entry: GetLeaderboardRankingsQuery["leaderboardRankings"][number] | undefined | null;
}

function LeaderboardEntry({ entry, forceMine, fallbackAvatarUrl }: LeaderboardEntryProps) {
  const { t } = useTranslation("leaderboards");

  const { id } = getAuthUser() ?? {};
  const isMine = forceMine || id === entry?.user.id;

  function getUserString() {
    if (isMine) {
      return entry ? t("leaderboards.you") : t("leaderboards.none_me");
    }

    return entry?.user.displayName ?? "--";
  }

  return (
    <ConditionalLink href={entry ? getUserUrl(entry.user) : undefined}>
      <div
        className={clsx(
          "w-full h-8 lg:h-9 flex items-center border-2 pr-4 rounded-full",
          isMine ? "border-purple" : "border-transparent",
          "text-xs leading-1.2 lg:text-sm lg:leading-1.2 font-medium"
        )}
      >
        <div
          className={clsx(
            isMine ? "bg-purple/30" : "bg-purple/10",
            "rounded-full pl-2 mr-3 flex items-center border border-purple"
          )}
        >
          <span className="w-6 text-center mr-1">{entry?.rank ?? "--"}</span>
          <div className="w-8 h-8 lg:w-9 lg:h-9 relative -my-[1px] -mr-[1px]">
            <Image
              src={entry?.user.avatarUrl ?? fallbackAvatarUrl ?? NO_IMAGE_URL}
              alt=""
              layout="fill"
              sizes="48px"
              className="rounded-full"
            />
          </div>
        </div>

        <div
          className={clsx(
            "flex max-w-full grow min-w-0",
            isMine ? "items-center justify-between" : "flex-col items-start"
          )}
        >
          <span className="truncate max-w-full">{getUserString()}</span>
          <span className={clsx(!isMine && "mt-1", "text-purple")}>{displayEntryScore(entry)}</span>
        </div>
      </div>
    </ConditionalLink>
  );
}

export default LeaderboardEntry;
